import { useEffect } from 'react';
import type { Arc } from '../plan/arc';

/**
 * The pointer's menu on a board row.
 *
 * Only what this surface can do. `Reveal in Obsidian` is in the spec and is
 * absent: a browser has no filesystem access, and `obsidian://` needs a local
 * absolute path the web cannot learn. A menu item that never works teaches
 * the wrong thing about the product.
 */
export function RowMenu({
  at,
  arc,
  heldBy,
  onOpen,
  onStatus,
  onSendToTop,
  onAddTo,
  onRemoveFrom,
  onClose,
}: {
  at: { x: number; y: number };
  arc: Arc;
  /** The storyline this arc already sits inside, if any. */
  heldBy: Arc | null;
  onOpen: () => void;
  onStatus: () => void;
  onSendToTop: () => void;
  /** Absent where nothing can be written. */
  onAddTo?: () => void;
  onRemoveFrom: (storyline: Arc) => void;
  onClose: () => void;
}) {
  useEffect(() => {
    const away = () => onClose();
    const id = setTimeout(() => document.addEventListener('pointerdown', away));
    return () => {
      clearTimeout(id);
      document.removeEventListener('pointerdown', away);
    };
  }, [onClose]);

  const run = (fn: () => void) => () => {
    fn();
    onClose();
  };

  return (
    <div
      className="plane-menu row-menu"
      style={{ left: at.x, top: at.y }}
      role="menu"
      onPointerDown={(e) => e.stopPropagation()}
    >
      <button type="button" onClick={run(onOpen)}>
        Open <kbd className="kbd">↵</kbd>
      </button>
      <button type="button" onClick={run(onStatus)}>
        Status… <kbd className="kbd">1–4</kbd>
      </button>
      <button type="button" onClick={run(onSendToTop)}>
        Send to top <kbd className="kbd">⌥↑</kbd>
      </button>
      <span className="plane-menu-sep" />
      {onAddTo && (
        <button type="button" onClick={run(onAddTo)}>
          Add to…
        </button>
      )}
      {heldBy && (
        <button type="button" onClick={run(() => onRemoveFrom(heldBy))}>
          Remove from {heldBy.title}
        </button>
      )}
      <span className="plane-menu-sep" />
      <button type="button" onClick={run(() => void navigator.clipboard?.writeText(arc.title))}>
        Copy name
      </button>
      <button type="button" onClick={run(() => void navigator.clipboard?.writeText(arc.path))}>
        Copy vault path
      </button>
    </div>
  );
}
